import { FC, forwardRef, ReactElement } from "react";

// Styles
import { Checkmark } from "@carbon/icons-react";

// Components
import { Box, Chip, ChipOwnProps } from "@mui/material";


interface TagProps {
    colour?: 'high-contrast' | 'grey' | 'cool-grey' | 'warm-grey' | 'red' | 'orange' | 'magenta' | 'purple' | 'blue' | 'cyan' | 'teal' | 'green' | 'outline';
    size?: 'extra-small' | 'small' | 'medium' | 'large';
    active?: boolean;
    label: string;
    icon?: ReactElement;
    index?: number;
    outlined?: boolean;
    showActiveTick?: boolean;
    onSelect?: () => void;
    sx?: ChipOwnProps['sx']
}

const Tag: FC<TagProps> = forwardRef(({
    size = 'small',
    colour,
    active = true,
    label,
    icon,
    index = 0,
    outlined = false,
    showActiveTick = false,
    onSelect,
    sx,
    ...props
}, ref) => {

    const classes = {
        'Tag': true,
        'Tag-extra-small': size == 'extra-small',
        'Tag-small': size == 'small',
        'Tag-medium': size == 'medium',
        'Tag-large': size == 'large',
      }
    
    const validClasses = Object.entries(classes)
        .filter(([,v]) => !!v)
        .map(([k,]) => k)
        .join(' ')

    // Based on the index, return the color for the avatar (10 colors available)
    const colors = [
        'blue',
        'green',
        'magenta',
        'cyan',
        'purple',
        'teal',
        'grey',
        // 'red',
        // 'high-contrast',
        // 'cool-grey',
        // 'warm-grey',
    ]

    const selectColour = active ? 'high-contrast' : 'outline' 
    const color = !!onSelect ? selectColour : colors[index % colors.length];

    return (
        // Wrapped in a Box to allow for the ref to be passed, as Chip does not accept a ref prop
        <Box ref={ref} {...props} display="inline-block">
            <Chip
                onClick={onSelect}
                className={validClasses}
                icon={showActiveTick && active ? <Checkmark style={{color: 'var(--white)'}} /> : icon}
                label={label}
                sx={{
                    backgroundColor: `var(--tag-${colour ?? color}-background)`,
                    color: `var(--tag-${colour ?? color}-color)`,
                    border: outlined || color === 'outline' ? `solid 1px var(--tag-${colour ?? color}-border)` : `solid 1px var(--tag-${colour ?? color}-background)`,
                    '&:hover': {
                        cursor: !!onSelect ? 'pointer' : 'default',
                        backgroundColor: `var(--tag-${colour ?? color}-hover)`,
                    },
                    '& .MuiChip-icon': {
                        marginRight: '2px',
                        marginLeft: 0,
                    },
                    ...sx,
                }}
            />
        </Box>
    )
});

export default Tag;
export type { TagProps };